import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { SeverityBadge, StatusBadge } from "@/components/Primitives";
import { Sparkles, Send } from "lucide-react";
import { toast } from "sonner";

export default function TicketDetail() {
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [body, setBody] = useState("");
  const [summary, setSummary] = useState("");
  const [rca, setRca] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);

  const load = () => api.get(`/tickets/${id}`).then(r => setT(r.data));
  useEffect(() => { load(); }, [id]);

  const addComment = async () => {
    if (!body.trim()) return;
    await api.post(`/tickets/${id}/comments`, { body });
    setBody("");
    load();
  };

  const change = async (field, val) => {
    await api.patch(`/tickets/${id}`, { [field]: val });
    toast.success("Updated");
    load();
  };

  const aiSummary = async () => { setLoadingAI(true); try { const r = await api.post(`/ai/ticket-summary/${id}`); setSummary(r.data.summary); } finally { setLoadingAI(false); } };
  const aiRca = async () => { setLoadingAI(true); try { const r = await api.post(`/ai/rca/${id}`); setRca(r.data); } finally { setLoadingAI(false); } };

  if (!t) return <div className="text-zinc-500 text-sm">Loading…</div>;

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      <div className="xl:col-span-2 space-y-4">
        <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5">
          <div className="flex items-center gap-3">
            <div className="text-zinc-500 font-mono text-sm">#{t.number}</div>
            <SeverityBadge level={t.priority} />
            <StatusBadge status={t.status} />
            <span className="text-xs text-zinc-500 ml-auto font-mono">SLA: {t.sla_deadline?.slice(0,16).replace("T"," ")}</span>
          </div>
          <h1 className="font-display text-2xl font-semibold tracking-tight mt-3">{t.title}</h1>
          <p className="text-sm text-zinc-400 mt-2">{t.description}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <select data-testid="ticket-status-update" value={t.status} onChange={e=>change("status", e.target.value)} className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-xs">
              {["open","in_progress","pending","closed"].map(s=><option key={s}>{s}</option>)}
            </select>
            <select data-testid="ticket-priority-update" value={t.priority} onChange={e=>change("priority", e.target.value)} className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-xs">
              {["critical","high","medium","low"].map(s=><option key={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5">
          <div className="font-display font-medium mb-3">Conversation</div>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {(t.comments || []).map(c => (
              <div key={c.id} className="border border-zinc-900 rounded p-3 bg-zinc-900/30">
                <div className="flex items-center gap-2 text-xs text-zinc-500"><span className="text-zinc-300 font-medium">{c.actor_name}</span><span className="font-mono">{c.at?.slice(0,16).replace("T"," ")}</span>{c.internal && <SeverityBadge level="info">internal</SeverityBadge>}</div>
                <div className="text-sm text-zinc-300 mt-2 whitespace-pre-wrap">{c.body}</div>
              </div>
            ))}
            {(t.comments || []).length === 0 && <div className="text-zinc-500 text-sm">No comments yet.</div>}
          </div>
          <div className="flex gap-2 mt-3">
            <textarea data-testid="ticket-comment-input" value={body} onChange={e=>setBody(e.target.value)} rows={2} placeholder="Add a note…" className="flex-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60" />
            <button data-testid="ticket-comment-submit" onClick={addComment} className="bg-blue-600 hover:bg-blue-700 rounded px-3 text-white text-sm flex items-center gap-1"><Send size={14} /> Send</button>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5">
          <div className="flex items-center gap-2 mb-3"><Sparkles size={14} className="text-blue-400" /><div className="font-display font-medium">AI Operations</div></div>
          <div className="flex gap-2 flex-wrap mb-3">
            <button data-testid="ai-summary-btn" onClick={aiSummary} disabled={loadingAI} className="bg-blue-600 hover:bg-blue-700 text-white text-xs rounded px-3 py-1.5">Summarize</button>
            <button data-testid="ai-rca-btn" onClick={aiRca} disabled={loadingAI} className="bg-zinc-900 border border-zinc-800 text-zinc-100 text-xs rounded px-3 py-1.5">Root Cause</button>
          </div>
          {summary && <pre className="text-xs whitespace-pre-wrap text-zinc-300 leading-relaxed max-h-64 overflow-auto border border-zinc-900 rounded p-3 mb-3" data-testid="ai-summary-text">{summary}</pre>}
          {rca && (
            <div className="space-y-2">
              <pre className="text-xs whitespace-pre-wrap text-zinc-300 leading-relaxed max-h-72 overflow-auto border border-zinc-900 rounded p-3">{rca.rca}</pre>
              {rca.related_tickets?.length > 0 && (
                <div className="text-[11px] text-zinc-500 font-mono">{rca.related_tickets.length} related historical tickets analyzed.</div>
              )}
            </div>
          )}
        </div>
        <div className="bg-zinc-950 border border-zinc-900 rounded-md p-5 text-xs space-y-2 text-zinc-400">
          <div className="font-display text-sm text-zinc-200 mb-2">Context</div>
          <div>Category: <span className="font-mono">{t.category}</span></div>
          <div>Client: <span className="font-mono">{t.client_id}</span></div>
          {t.asset_id && <div>Asset: <Link to={`/assets/${t.asset_id}`} className="font-mono text-blue-400 hover:underline">{t.asset_id}</Link></div>}
          <div>Created: <span className="font-mono">{t.created_at?.slice(0,16).replace("T"," ")}</span></div>
        </div>
      </div>
    </div>
  );
}
